import React from 'react';
import ReactDOM from 'react-dom/client';

import ConfirmDialog from '~/common/components/dialogs/ConfirmDialog';
import MessageDialog from '~/common/components/dialogs/MessageDialog';
import Body from '~/common/components/typography/Body';

import type { Root } from 'react-dom/client';

import type { ConfirmProps } from '~/common/components/dialogs/ConfirmDialog';
import type { MessageProps } from '~/common/components/dialogs/MessageDialog';

const DIALOG_DOM_ID = '__dialog__';

const message = ({
  title,
  content,
  confirmLabel,
  maskClosable,
}: Omit<MessageProps, 'children'> & { content: React.ReactNode }): Promise<void> => {
  const place = document.getElementById(DIALOG_DOM_ID);

  let dialogResolver: (() => void) | null = null;

  const open = () => {
    if (place) {
      const root = ReactDOM.createRoot(place);
      root.render(
        <MessageDialog
          title={title}
          confirmLabel={confirmLabel}
          maskClosable={maskClosable}
          onConfirm={() => handleClose(root)}
        >
          <Body>{content}</Body>
        </MessageDialog>,
      );
    }

    return new Promise<void>(resolve => {
      dialogResolver = resolve;
    });
  };

  const handleClose = (root: Root) => {
    root.unmount();

    if (dialogResolver) {
      dialogResolver();
    }
  };

  return open();
};

const confirm = ({
  title,
  content,
  confirmLabel,
  cancelLabel,
}: Omit<ConfirmProps, 'children'> & { content: React.ReactNode }) => {
  const place = document.getElementById(DIALOG_DOM_ID);

  let confirmResolver: ((value: boolean) => void) | null = null;

  const open = () => {
    if (place) {
      const root = ReactDOM.createRoot(place);
      root.render(
        <ConfirmDialog
          title={title}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          onCancel={() => handleCancel(root)}
          onConfirm={() => handleConfirm(root)}
        >
          <Body>{content}</Body>
        </ConfirmDialog>,
      );
    }

    return new Promise<boolean>(resolve => {
      confirmResolver = resolve;
    });
  };

  const handleClose = (root: Root) => {
    root.unmount();
  };

  const handleConfirm = (root: Root) => {
    if (confirmResolver) {
      confirmResolver(true);
    }
    handleClose(root);
  };

  const handleCancel = (root: Root) => {
    if (confirmResolver) {
      confirmResolver(false);
    }
    handleClose(root);
  };

  return open();
};

const dialog = { confirm, message };

export default dialog;
